import { CheckboxContainer, Container, RowContainer } from "./TreeNode.styles";
import UI from "@mile-meeting/mile-ui";

export interface OrgNode {
  _children: Record<string, OrgNode>;
}

export interface TreeNodeProps {
  name: string;
  node: OrgNode;
  level: number;
  path: string;
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  selected: Record<string, boolean | undefined>;
  setSelected: React.Dispatch<React.SetStateAction<Record<string, boolean | undefined>>>;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  name,
  node,
  level,
  path,
  expanded,
  setExpanded,
  selected,
  setSelected,
}) => {
  const hasChildren = Object.keys(node._children).length > 0;
  const isExpanded = expanded[path];

  const toggleExpand = () => {
    setExpanded((prev) => ({ ...prev, [path]: !isExpanded }));
  };

  //* INFO: 선택 상태를 토글하는 함수
  const toggleSelect = () => {
    const isSelected = selected[path];
    const newSelected = { ...selected };

    //* 하위 노드 전체를 업데이트하는 재귀 함수
    const updateChildren = (node: OrgNode, basePath: string, selectedValue: boolean) => {
      newSelected[basePath] = selectedValue;
      Object.entries(node._children).forEach(([childName, childNode]) => {
        const childPath = `${basePath}/${childName}`;
        updateChildren(childNode, childPath, selectedValue);
      });
    };

    //* 상위 노드들의 상태를 업데이트하는 함수
    const updateParents = (currentPath: string) => {
      const parts = currentPath.split("/").filter(Boolean);

      for (let i = parts.length - 1; i > 0; i--) {
        const parentPath = "/" + parts.slice(0, i).join("/");
        const parentDepth = i;

        const childPaths = Object.keys(selected).filter((key) => {
          const keyParts = key.split("/").filter(Boolean);
          return keyParts.length === parentDepth + 1 && "/" + keyParts.slice(0, parentDepth).join("/") === parentPath;
        });

        const states = childPaths.map((key) => newSelected[key]);

        const noneSelected = states.every((v) => v === false);
        const someSelected = states.some((v) => v === true || v === undefined);

        if (someSelected) {
          newSelected[parentPath] = undefined;
        } else if (noneSelected) {
          newSelected[parentPath] = false;
        }
      }
    };
    updateChildren(node, path, isSelected === undefined ? false : !isSelected);
    updateParents(path);
    setSelected(newSelected);
  };

  return (
    <Container>
      <RowContainer style={{ marginLeft: `${level * 24}px`, alignItems: "center" }}>
        <CheckboxContainer>
          <UI.CheckBox
            size="small"
            selected={selected[path] === undefined ? true : !!selected[path]}
            type={selected[path] === undefined ? "indeterminate" : "selected"}
            onChange={toggleSelect}
          />
          <UI.Typography type="body02 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
            {name}
          </UI.Typography>
        </CheckboxContainer>
        {hasChildren && (
          <UI.IconButton.Normal
            type="secondary"
            icon={isExpanded ? "arrowUp" : "arrowDown"}
            size={12}
            color="var(--alias-color-label-assistive)"
            onClick={toggleExpand}
          />
        )}
      </RowContainer>
      {hasChildren &&
        isExpanded &&
        Object.entries(node._children).map(([childName, childNode]) => (
          <TreeNode
            key={path + "/" + childName}
            name={childName}
            node={childNode}
            level={level + 1}
            path={path + "/" + childName}
            expanded={expanded}
            setExpanded={setExpanded}
            selected={selected}
            setSelected={setSelected}
          />
        ))}
    </Container>
  );
};

export default TreeNode;
